import { getAssetPath } from "@commonUtils"
import { keysToCamel } from "@lib/snakeTocamel"

export const parseWidgets = (data) => {
    let widgets = {}
    if (data?.footer_cta) {
        widgets["actionButton"] = {
            targetUrl: "/done-in-one/rx-order-preview",
            text: data?.footer_cta?.text || "",
        }
    }
    if (data?.upload_prescription) {
        widgets["uploadPrescription"] = {
            header: data?.upload_prescription?.header,
            subHeader: data?.upload_prescription?.sub_header,
            uploadIcon: data?.upload_prescription?.icon,
            beforeUploadingCta: data?.upload_prescription?.before_uploading_cta?.text,
            afterUploadingCta: data?.upload_prescription?.after_uploading_cta?.text,
            handleUploadFunctionMap: "handleUpload",
        }
        let cartNotEmptyData = data?.upload_prescription?.cart_not_empty_dialog
        if (cartNotEmptyData) {
            widgets["cartNotEmptyDialog"] = {
                header: cartNotEmptyData?.header,
                subHeader: cartNotEmptyData?.description,
                icon: cartNotEmptyData?.icon,
                resolution: cartNotEmptyData?.resolution,
                primaryCta: {
                    text: cartNotEmptyData?.confirm_cta?.text || "",
                    functionMap: "placeNewRequest",
                },
                secondaryCta: {
                    text: cartNotEmptyData?.cancel_cta?.text || "",
                    functionMap: "viewOldRequest",
                    action: cartNotEmptyData?.cancel_cta?.action,
                    targetUrl: cartNotEmptyData?.cancel_cta?.details?.target_url,
                },
            }
        }
    }
    if (data?.remove_this_prescription) {
        widgets["removePrescriptionDialog"] = {
            header: data?.remove_this_prescription?.header,
            subHeader: data?.remove_this_prescription?.description,
            icon: data?.remove_this_prescription?.icon,
            primaryCta: {
                text: data?.remove_this_prescription?.confirm_cta?.text || "",
                functionMap: "removePrescription",
            },
            secondaryCta: {
                text: data?.remove_this_prescription?.cancel_cta?.text || "",
                functionMap: "closeDialog",
            },
        }
    }
    if (data?.upload_prescription_footnotes) {
        widgets["footnotes"] = {
            subHeader: data?.upload_prescription_footnotes?.sub_text,
            data: [
                {
                    header: "",
                    displayText: `<u>${data?.upload_prescription_footnotes?.sub_text_cta || ""}</u>`,
                },
            ],
        }
    }
    if (data?.how_it_works) {
        let howItWorks = data?.how_it_works
        widgets["howItWorks"] = {
            header: howItWorks?.header,
            steps: howItWorks?.values.map((value) => {
                return {
                    header: value.header,
                    imageUrl: value.icon,
                    subHeader: value.sub_header,
                }
            }),
            ctaText: howItWorks?.cta?.text,
        }
    }
    if (data?.valid_prescription_guide) {
        widgets["validPrescriptionMarkers"] = {
            ctaLabel: data?.valid_prescription_guide?.cta?.text || "",
            header: data?.valid_prescription_guide?.header,
            details: data?.valid_prescription_guide?.values.map((value) => {
                return {
                    header: value.header,
                    imageUrl: value.icon,
                    subHeader: value.text,
                }
            }),
        }
    }
    if (data?.go_back_dialog) {
        widgets["confirmBack"] = {
            header: data?.go_back_dialog?.header,
            subHeader: data?.go_back_dialog?.description,
            icon: data?.go_back_dialog?.icon,
            resolution: data?.go_back_dialog?.resolution,
            primaryCta: {
                text: data?.go_back_dialog?.confirm_cta?.text,
                functionMap: "removePrescription",
            },
            secondaryCta: {
                text: data?.go_back_dialog?.cancel_cta?.text,
                functionMap: "closeDialog",
            },
        }
    }
    if (data?.retry_upload_dialog) {
        widgets["retryUpload"] = {
            header: data?.retry_upload_dialog?.header,
            subHeader: data?.retry_upload_dialog?.description,
            icon: data?.retry_upload_dialog?.icon,
            resolution: data?.retry_upload_dialog?.resolution,
            primaryCta: {
                text: data?.retry_upload_dialog?.confirm_cta?.text,
                functionMap: "retryUpload",
            },
            secondaryCta: {
                text: data?.retry_upload_dialog?.cancel_cta?.text,
                functionMap: "closeDialog",
            },
        }
    }

    if (data?.attentive_info) {
        widgets["attentiveInfo"] = {
            header: data?.attentive_info?.header,
            subHeader: data?.attentive_info?.sub_header,
            targetUrl: data?.attentive_info?.url,
        }
    }
    if (data?.attached_prescriptions) {
        widgets["attachedPrescription"] = {
            listOfImages: data.attached_prescriptions?.map((item) => {
                return {
                    image: {
                        url: item.icon,
                        xxhighUrl: item.file_url,
                        alt: "prescription",
                        id: item.id,
                        type:
                            item.file_url?.includes("pdf") || item.file_url?.includes("application")
                                ? "pdf"
                                : "image",
                        cta: {
                            functionMap: "prescriptionClicked",
                        },
                        documentUrl: item.file_url,
                    },
                    iconOnImage: {
                        url: getAssetPath("svgs/icons/cross_icon_primary_18px.svg"),
                        alt: "remove prescription",
                        cta: {
                            functionMap: "removeIconClick",
                        },
                    },
                }
            }),
        }
    }
    return widgets
}

/**
 * returning already attached prescription Images for your Prescriptions Page
 * @param {object} gallery
 * @return {object} for ImageGallery Widget to show already selected images
 */
const getSelectedImageIdObject = (gallery) => {
    let imagesObject = {}
    if (gallery?.length) {
        gallery.forEach((item) => {
            item.images?.forEach((image) => {
                if (image.selected) {
                    imagesObject[image.id] = image
                }
            })
        })
    }
    return imagesObject
}
/**
 * Creating parsed data for widgets for which this function is called
 * @param {object} data
 * @return {object} for ImageGallery Widget
 */
export const parseYourPrescriptions = (
    data,
    pageMeta,
    currentPageNumber = 0,
    existingPrescriptions = [],
    existingSelectedImages = []
) => {
    let hasMore = false
    let yourPrescriptions = {
        galleries: data.prescriptions?.gallery?.map((item) => {
            return {
                subText: item.header,
                images: item.images.map((image) => {
                    return {
                        src:
                            image?.type?.includes("pdf") || image?.type?.includes("application")
                                ? image.icon
                                : image.url,
                        id: image.id,
                        documentUrl: image.url,
                        selected: image.selected,
                        statusText: image.status_text,
                        type:
                            image?.type?.includes("pdf") || image?.type?.includes("application")
                                ? "pdf"
                                : "image",
                        header: { text: image.status_text, icon: image.status_icon },
                        description: image.status_description,
                        statusIcon: image.status_icon || "",
                    }
                }),
            }
        }),
        selectedImageIdObject: getSelectedImageIdObject(data.prescriptions?.gallery),
        stickyBottomButtonCtaLabel: data.cta?.text,
        rxAttachLimitData: data.rx_attach_limit_data,
    }
    if (pageMeta.total_pages > currentPageNumber + 1) {
        hasMore = true
    }
    let updatedExistingGallery
    if (currentPageNumber > 0) {
        let galleries = yourPrescriptions.galleries
        let tempExistingPrescriptions = existingPrescriptions
        let length = tempExistingPrescriptions.length
        if (tempExistingPrescriptions[length - 1].subText === galleries[0].subText) {
            galleries[0].images = tempExistingPrescriptions[length - 1].images.concat(galleries[0].images)
            updatedExistingGallery = tempExistingPrescriptions.slice(0, -1)
        } else {
            updatedExistingGallery = tempExistingPrescriptions
        }
        let combinedGalleries = updatedExistingGallery.concat(galleries)
        yourPrescriptions.galleries = combinedGalleries
        let selectedImageIdObject = yourPrescriptions.selectedImageIdObject
        let combinedSelectedImageIdObject = { ...existingSelectedImages, ...selectedImageIdObject }
        yourPrescriptions.selectedImageIdObject = combinedSelectedImageIdObject
    }
    return { yourPrescriptions: yourPrescriptions, currentPageNumber: currentPageNumber, hasMore: hasMore }
}

export const parseUploadLimit = (data) => {
    return {
        message: data?.message,
        limit: data?.attachment_limit,
        totalLimit: data?.total_limit,
    }
}

export const parseMemberListData = (data) => {
    const { member_list, cta } = data

    return {
        header: member_list.header,
        subHeader: member_list.sub_header,
        disclaimer: member_list.disclaimer,
        cta,
        noPrescription: keysToCamel(member_list.no_prescription),
        memberList: keysToCamel(member_list.list),
    }
}

export const getSelectedMemberData = (data = []) => {
    const selectedMember = data.find(({ isSelected }) => isSelected)

    return {
        label: selectedMember?.label || "",
        id: selectedMember?.id || "",
        details: selectedMember?.details || "",
        secondary_details: selectedMember?.secondary_details || "",
        isSelected: !!selectedMember,
        isDisabled: false,
        name: "member",
    }
}
